import {
  Controller,
  Control,
  FieldValues,
  DeepMap,
  FieldError,
  RegisterOptions,
} from 'react-hook-form';
import {
  Box,
  Checkbox,
  Chip,
  InputLabel,
  ListItemText,
  Select,
  styled,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const PREFIX = 'ChipSelectMenu';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(0.375),
    paddingBottom: 0,
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.lightActionDisabled26p.main}`,
  borderRadius: '16px',
  height: '24px',
  '& .MuiChip-label': {
    color: theme.palette.lightTextPrimary.main + ' !important',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  '& .MuiChip-deleteIcon': {
    fontSize: '16px',
    color: theme.palette.lightActionDisabled54p.main,
    marginRight: '4px',
    '&:hover': {
      color: theme.palette.lightActionDisabled60p.main,
    },
  },
}));

interface ChipSelectMenuProps {
  label: string;
  name: string;
  values: any[];
  valueAttr: string;
  labelAttr: string;
  control: Control<any>;
  required?: boolean;
  errors?: DeepMap<FieldValues, FieldError>;
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >;
  defaultValue?: any;
  className?: string;
}

export default function ChipSelectMenu({
  label,
  name,
  control,
  values,
  valueAttr,
  labelAttr,
  required = false,
  errors,
  rules = undefined,
  defaultValue = undefined,
  className = undefined,
}: ChipSelectMenuProps) {
  const getChipLabel = (id: any): string | undefined => {
    const val = values.find((v) => v[valueAttr] === id);
    return val ? val[labelAttr] : undefined;
  };

  const labelId = `${name}-label`;

  return (
    <Controller
      name={name as any}
      control={control}
      {...(defaultValue !== undefined ? { defaultValue } : {})}
      rules={rules}
      render={({ field }) => {
        // MUI throws if a `multiple` Select receives a non-array value
        const value: any[] = Array.isArray(field.value)
          ? field.value
          : field.value == null || field.value === ''
            ? []
            : [field.value];

        return (
          <FormControl className={className}>
            <InputLabel id={labelId} variant="outlined" required={required}>
              {label}
            </InputLabel>
            <Select
              {...field}
              value={value}
              required={required}
              labelId={labelId}
              label={label}
              variant="outlined"
              multiple={true}
              error={errors ? !!errors[name] : false}
              onChange={(evt) => field.onChange(evt.target.value)}
              renderValue={(selected) => {
                return (
                  <StyledBox className={classes.container}>
                    {selected?.map((val: any) => {
                      const label = getChipLabel(val);
                      return label ? (
                        <StyledChip
                          key={val}
                          label={label}
                          variant="outlined"
                          size="medium"
                          onDelete={() => {
                            const newValue = (selected || []).filter(
                              (v: any) => v !== val,
                            );
                            field.onChange(newValue);
                          }}
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      ) : null;
                    })}
                  </StyledBox>
                );
              }}
              IconComponent={KeyboardArrowDownIcon}>
              {values.map((val) => {
                return (
                  <MenuItem value={val[valueAttr]} key={val[valueAttr]}>
                    <Checkbox checked={value.includes(val[valueAttr])} />
                    <ListItemText
                      primary={val.locationName || val[labelAttr]}
                      secondary={val.locationName && val[labelAttr]}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        );
      }}
    />
  );
}
